// src/pages/EcrfPage.js
//
// SourceX Roadmap Phase 1b — upload a blank eCRF, run extraction, review what
// came back.
//
// This is a REVIEW surface, not an authoring one. The extraction is the study's
// structured eCRF source of truth; a coordinator's job here is to confirm the
// forms/fields/branching were read correctly before Phase 2 uses them for the
// SoA join. Editing individual fields comes with that phase — right now the
// value is seeing what the parse produced and what it flagged.

import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft,
  Loader2,
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  FileText,
  GitBranch,
  Eye,
  RefreshCw,
  X,
} from "lucide-react";
import { db } from "../firebase";
import { getSourcexTemplatesPath } from "../services/firestorePaths";
import {
  uploadAndCreateEcrf,
  runExtraction,
  collectBatch,
  listenEcrfs,
  listenEcrfExtraction,
} from "../services/ecrfExtractionService";
import { listenProtocols } from "../services/protocolExtractionService";
import StepProgressOverlay from "../components/sourcex/StepProgressOverlay";
import { nextWorkflowStep, protocolApproved } from "../services/sourcexWorkflow";
import { ECRF_BATCH_ENABLED, PROTOCOL_COMPARE_ENABLED } from "../config/featureFlags";
import { listenVersions } from "../services/protocolVersionService";
import { sourcexVersionGuidance } from "../services/protocolVersions";
import AiHelperNotice from "../components/AiHelperNotice";
import {
  ECRF_STATUS,
  LOW_CONFIDENCE_THRESHOLD,
  countEcrfFields,
  lowConfidenceEcrfCount,
} from "../services/ecrfTypes";

const PageBackground = () => (
  <div className="fixed inset-0 -z-10 overflow-hidden">
    <img
      src="/bg_ai.jpg"
      alt=""
      aria-hidden="true"
      className="absolute inset-0 w-full h-full object-cover"
    />
    <div className="absolute inset-0 bg-gradient-to-br from-[#070418]/90 via-[#0c0726]/85 to-[#04030f]/95" />
    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(124,58,237,0.25),_transparent_55%)]" />
  </div>
);

const card =
  "rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-xl";

const STATUS_STYLE = {
  [ECRF_STATUS.UPLOADED]: "bg-white/[0.06] border-white/15 text-slate-300",
  [ECRF_STATUS.PROCESSING]: "bg-amber-400/10 border-amber-400/30 text-amber-200",
  partial: "bg-amber-400/10 border-amber-400/30 text-amber-200",
  // Queued with the batch reader — blue rather than amber, because nothing is
  // stuck and nobody needs to wait on the page.
  [ECRF_STATUS.BATCH_PENDING]: "bg-blue-400/10 border-blue-400/30 text-blue-200",
  // Finished, not pending — see ECRF_STATUS_LABEL.
  [ECRF_STATUS.NEEDS_REVIEW]:
    "bg-emerald-400/10 border-emerald-400/30 text-emerald-200",
  [ECRF_STATUS.APPROVED]:
    "bg-emerald-400/10 border-emerald-400/30 text-emerald-200",
  [ECRF_STATUS.FAILED]: "bg-rose-500/10 border-rose-400/30 text-rose-200",
};

/**
 * Statuses that mean the extraction is DONE and nothing is being asked of
 * anybody. These print no pill at all — see the eCRF list.
 *
 * `needs_review` is the pipeline's word for "finished, nobody has signed it
 * off". On the protocol that is a real instruction; an eCRF has no such gate and
 * this page offers no approve action, so the words described a job that does not
 * exist and made a finished upload look unfinished. The stored status is
 * untouched — this only decides what the row draws.
 */
const SETTLED_STATUSES = new Set([
  ECRF_STATUS.NEEDS_REVIEW,
  ECRF_STATUS.APPROVED,
]);

const StatusPill = ({ status }) => (
  <span
    className={`inline-flex items-center px-2 py-0.5 rounded-lg text-[11px] border ${
      STATUS_STYLE[status] || STATUS_STYLE[ECRF_STATUS.UPLOADED]
    }`}
  >
    {String(status || "uploaded").replace(/_/g, " ")}
  </span>
);

const money = (n) =>
  typeof n === "number" ? `$${n < 0.01 ? n.toFixed(4) : n.toFixed(2)}` : "—";

/**
 * "12 Sep 2026, 14:05" from whatever the field happens to hold.
 *
 * `uploadedAt` is a Firestore serverTimestamp, which is NULL for the moment
 * between the local write and the server's acknowledgement — a just-uploaded
 * eCRF therefore has no time yet and must not render "Invalid Date". Older
 * documents predate the field entirely and fall back to `createdAt`.
 */
const formatWhen = (value) => {
  if (!value) return "";
  const d = typeof value?.toDate === "function" ? value.toDate() : new Date(value);
  return Number.isNaN(d.getTime())
    ? ""
    : d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
};

/** Who uploaded it, and when — the two facts a monitor asks about a file. Quiet
 * and underneath: it is provenance, read once when somebody asks "who put this
 * here?", never scanned. Mirrors the same line on the protocol list. */
const UploadedLine = ({ item }) => {
  const when = formatWhen(item.uploadedAt || item.createdAt);
  const who = item.createdBy || "";
  if (!when && !who) return null;
  return (
    <span className="block mt-0.5 text-[10px] text-slate-500">
      Uploaded {when || "just now"}
      {who && (
        <>
          {" by "}
          <span className="text-slate-400">{who}</span>
        </>
      )}
    </span>
  );
};

const primaryBtn =
  "inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-500 text-sm font-semibold text-white hover:brightness-110 disabled:opacity-50 disabled:cursor-not-allowed transition";
const quietBtn =
  "inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-white/[0.05] border border-white/12 text-sm text-slate-300 hover:bg-white/[0.1] hover:text-white transition";

/** The hidden-input upload control, as whatever the caller wants it to look
 * like. Three call sites need it (the empty-state hero, the header and the
 * landing modal), and a file input cannot be shared between them. */
function UploadButton({ busy, onFile, className, children }) {
  return (
    <label className={`${className} ${busy ? "cursor-wait" : "cursor-pointer"}`}>
      {children}
      <input
        type="file"
        accept="application/pdf,.pdf"
        disabled={busy}
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = ""; // allow re-selecting the same file
          onFile(file);
        }}
      />
    </label>
  );
}

/** A centred dialog. Escape and the backdrop close it; every modal on this page
 * is advisory, so there is always a real way out. Mirrors ProtocolsPage. */
function Modal({ onClose, title, children, footer, wide }) {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-[90] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <motion.div
        initial={{ y: 8, scale: 0.98 }}
        animate={{ y: 0, scale: 1 }}
        className={`w-full ${
          wide ? "max-w-2xl" : "max-w-md"
        } rounded-2xl border border-white/12 bg-[#0c0826] shadow-[0_24px_70px_-20px_rgba(0,0,0,0.9)] overflow-hidden`}
      >
        <div className="flex items-start gap-3 px-5 pt-5">
          <h2 className="min-w-0 flex-1 text-base font-semibold text-white leading-snug">
            {title}
          </h2>
          <button
            onClick={onClose}
            aria-label="Close"
            className="shrink-0 w-7 h-7 rounded-lg flex items-center justify-center text-slate-400 hover:text-white hover:bg-white/10 transition"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        {children && (
          <div className="px-5 pt-2 pb-1 text-sm text-slate-300 max-h-[60vh] overflow-y-auto">
            {children}
          </div>
        )}
        <div className="flex flex-wrap items-center justify-end gap-2 px-5 py-4 mt-2 border-t border-white/10 bg-white/[0.02]">
          {footer}
        </div>
      </motion.div>
    </motion.div>
  );
}

export default function EcrfPage() {
  const { studyId } = useParams();
  const navigate = useNavigate();

  // null while the first snapshot is in flight — distinct from [], which means
  // "confirmed nothing here". The landing modal must not fire on a study whose
  // eCRFs simply have not arrived yet.
  const [ecrfs, setEcrfs] = useState(null);
  // Queue the read instead of running it now (~half price). Only offered when
  // both this client and the server have batch enabled — see featureFlags.js.
  const [queueBatch, setQueueBatch] = useState(false);
  const [collecting, setCollecting] = useState(false);
  // A ref as well as state: the auto-collect effect and the "Check now" button
  // can fire in the same tick, and state has not updated by then.
  const collectingRef = useRef(false);
  // eCRF ids we have already auto-collected in this mount, so a re-render (the
  // list is a live listener) cannot re-fire the check in a loop.
  const autoCollectedRef = useRef(new Set());
  const [protocols, setProtocols] = useState(null);
  const [hasAssembled, setHasAssembled] = useState(false);
  // Protocol versions: "next step" never sends anyone to an older version.
  const [versions, setVersions] = useState([]);
  useEffect(() => {
    if (!PROTOCOL_COMPARE_ENABLED || !studyId) return undefined;
    return listenVersions(studyId, setVersions, () => setVersions([]));
  }, [studyId]);
  const [assembledChecked, setAssembledChecked] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [extraction, setExtraction] = useState(null);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(null);
  const [error, setError] = useState("");
  const [openForms, setOpenForms] = useState({});
  // The eCRF uploaded in THIS visit — drives the "now go to step 3" modal.
  const [justUploadedId, setJustUploadedId] = useState(null);
  const [landingOpen, setLandingOpen] = useState(false);
  const [warningsOpen, setWarningsOpen] = useState(false);
  const [formsOpen, setFormsOpen] = useState(false);
  // An extraction that stopped, and which eCRF it was, so the overlay can offer
  // Resume without the user hunting for the row.
  const [stalledError, setStalledError] = useState("");
  const [stalledId, setStalledId] = useState(null);

  useEffect(() => {
    if (!studyId) return undefined;
    return listenEcrfs(
      studyId,
      (rows) => {
        setEcrfs(rows);
        setSelectedId((cur) => cur || (rows[0] ? rows[0].id : null));
      },
      (e) => {
        setError(e.message || "Could not load eCRFs.");
        // Out of `null` even on failure, or the page sits on "Loading…" with
        // the error above it and no way to upload anything.
        setEcrfs([]);
      }
    );
  }, [studyId]);

  // Collect any finished batch as soon as the page sees one queued. This is why
  // the batch path needs no scheduled job — which matters, because scheduled
  // jobs are off in staging and so could never be rehearsed there. Guarded by a
  // ref so the live listener re-rendering cannot turn this into a poll loop.
  useEffect(() => {
    if (!ecrfs || !ecrfs.length) return;
    const pending = ecrfs.find(
      (e) => e.status === ECRF_STATUS.BATCH_PENDING && !autoCollectedRef.current.has(e.id)
    );
    if (!pending) return;
    autoCollectedRef.current.add(pending.id);
    checkBatch(pending.id);
    // checkBatch is stable enough for this purpose and intentionally omitted:
    // including it would re-run the effect on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ecrfs]);

  // The other two steps' state, read only to answer "where should this person
  // be instead?". Failures are swallowed: not knowing what comes next is a
  // reason to offer less guidance, never a reason to break step 2.
  useEffect(() => {
    if (!studyId) return undefined;
    return listenProtocols(studyId, setProtocols, () => setProtocols([]));
  }, [studyId]);

  useEffect(() => {
    if (!studyId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDoc(
          doc(db, getSourcexTemplatesPath(), `${studyId}__assembled`)
        );
        if (!cancelled) setHasAssembled(snap.exists());
      } catch (_) {
        /* treated as "not built" — see above */
      } finally {
        if (!cancelled) setAssembledChecked(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [studyId]);

  useEffect(() => {
    if (!studyId || !selectedId) {
      setExtraction(null);
      return undefined;
    }
    return listenEcrfExtraction(studyId, selectedId, setExtraction, (e) =>
      setError(e.message || "Could not load the extraction.")
    );
  }, [studyId, selectedId]);

  const list = ecrfs || [];
  const selected = list.find((e) => e.id === selectedId) || null;
  const loading = ecrfs === null || protocols === null || !assembledChecked;
  // Has step 1 been FINISHED? Uploaded AND approved — see protocolApproved in
  // services/sourcexWorkflow, which step 3 asks the same question of.
  const step1Done = protocolApproved(protocols);
  // Uploaded but not signed off yet. Worth telling apart from "nothing here at
  // all": the two need completely different instructions, and sending somebody
  // who has already uploaded back to the upload page reads as though their
  // upload was lost.
  const protocolAwaitingApproval =
    !step1Done && (protocols || []).length > 0;

  // Where this person probably meant to go. `currentPath` is this page, so the
  // helper never answers "go to the page you are already on" — see
  // services/sourcexWorkflow.
  const nextStep = useMemo(
    () =>
      nextWorkflowStep({
        studyId,
        protocols: protocols || [],
        ecrfs: list,
        hasAssembled,
        currentPath: `/sourcex/ecrf/${studyId}`,
        ...(PROTOCOL_COMPARE_ENABLED
          ? (({ isLocked, readFirst }) => ({ isLocked, readFirst }))(sourcexVersionGuidance(protocols || [], versions, { docsBuilt: hasAssembled }))
          : {}),
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [studyId, protocols, ecrfs, hasAssembled, versions]
  );

  // "You have already done this" — raised ONCE, on the first load that knows
  // enough to say so. A ref rather than state so re-renders (and the live
  // snapshots behind them) cannot reopen a modal the user has closed.
  const landingShown = useRef(false);
  useEffect(() => {
    if (landingShown.current || loading) return;
    landingShown.current = true;
    if (list.length > 0 || hasAssembled) setLandingOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, ecrfs, hasAssembled]);

  const handleUpload = async (file) => {
    if (!file) return;
    if (!/pdf$/i.test(file.type) && !/\.pdf$/i.test(file.name)) {
      setError("Please choose a PDF.");
      return;
    }
    setError("");
    setBusy(true);
    setProgress(null);
    // Whatever brought them here, they have now chosen to upload — a stale
    // "already uploaded?" modal reopening over the result would be nonsense.
    setLandingOpen(false);
    try {
      const { ecrfId } = await uploadAndCreateEcrf(studyId, file);
      setSelectedId(ecrfId);
      setJustUploadedId(ecrfId);
      await extract(ecrfId, { batch: queueBatch });
    } catch (e) {
      setError(e.message || "Upload failed.");
    } finally {
      setBusy(false);
    }
  };

  /**
   * Check a queued batch once and collect it if the results are ready.
   *
   * Deliberately not a poll loop. A batch can take hours, so the page must
   * never sit on one — this runs on load and on demand, and the eCRF list is a
   * live listener, so a successful collect redraws the row on its own.
   */
  const checkBatch = async (ecrfId) => {
    if (collectingRef.current) return;
    collectingRef.current = true;
    setCollecting(true);
    try {
      await collectBatch(studyId, ecrfId);
    } catch (e) {
      // A failed check is not a failed extraction — the batch is still queued.
      // Say so quietly rather than turning the page red.
      console.warn("batch check failed:", e && e.message);
    } finally {
      collectingRef.current = false;
      setCollecting(false);
    }
  };

  const extract = async (ecrfId, { batch = false } = {}) => {
    setError("");
    setStalledError("");
    setStalledId(ecrfId);
    setBusy(true);
    try {
      // runExtraction drives the resume loop: a large eCRF can exceed the 540s
      // function limit, and completed chunks are reused rather than re-read.
      //
      // In batch mode it returns as soon as the read is QUEUED — the results
      // land minutes-to-hours later, so nothing sits here waiting. The eCRF list
      // is a live listener, and opening this page collects a finished batch.
      const r = await runExtraction(studyId, ecrfId, (p) => setProgress(p), {
        mode: batch ? "batch" : undefined,
      });
      if (r && r.batchPending) {
        setError("");
        setStalledId(null);
      }
    } catch (e) {
      const message = e.message || "Extraction failed.";
      setError(message);
      // Keep the full-screen overlay up rather than dropping the user back on a
      // page with a red line at the top. Resuming is the one thing worth doing
      // here and the finished chunks make it cheap — see StepProgressOverlay.
      setStalledError(message);
    } finally {
      setBusy(false);
    }
  };

  const stats = useMemo(() => {
    if (!extraction) return null;
    return {
      forms: (extraction.forms || []).length,
      fields: countEcrfFields(extraction),
      lowConf: lowConfidenceEcrfCount(extraction),
      unresolved: extraction.unresolved_branch_count || 0,
      warnings: (extraction.warnings || []).length,
      usage: extraction.usage || null,
    };
  }, [extraction]);

  /**
   * Every complaint the extraction has, in one list.
   *
   * They used to be three separate blocks down the page — a fallback-model
   * banner, a dropped-branches paragraph, and a bulleted list of up to forty
   * warnings — which made the page read as a wall of problems. None of them
   * blocks step 3, and none of them is a coordinator's job to fix: they are
   * notes for whoever tunes the pipeline. So they collapse behind one small
   * button and the page gets on with pointing at the next step.
   *
   * Nothing is dropped. Every line that used to be on the page is in here.
   */
  const warnings = useMemo(() => {
    if (!extraction) return [];
    const out = [];
    if ((extraction.degradedVendors || []).length > 0) {
      out.push({
        type: "fallback_model",
        message: `The intended model (${extraction.degradedVendors.join(
          ", "
        )}) was unreachable, so part of this eCRF was read with the lower-accuracy fallback. The result is usable, but re-run the extraction once the provider is fixed — check CLAUDE_VISION_ENABLED, CLAUDE_VISION_PROVIDER and the ANTHROPIC_API_KEY secret.`,
        tone: "bad",
      });
    }
    const dropped = extraction.unresolved_branch_count || 0;
    if (dropped > 0) {
      out.push({
        type: "dropped_branches",
        message: `${dropped} branching rule${
          dropped === 1 ? "" : "s"
        } could not be matched to a field and ${
          dropped === 1 ? "was" : "were"
        } dropped. Those fields will always be shown rather than being hidden incorrectly — check them against the eCRF.`,
      });
    }
    (extraction.warnings || []).forEach((w) => out.push(w));
    return out;
  }, [extraction]);

  // The eCRF just uploaded, and whether its extraction has settled. Unlike the
  // protocol pipeline this one runs in the foreground (runExtraction resolves
  // when it is done), so `busy` is the whole of "still working".
  const uploaded = list.find((e) => e.id === justUploadedId) || null;

  return (
    <div className="sourcex-page relative min-h-screen w-full text-slate-100 font-[Manrope] px-4 sm:px-6 py-6">
      <PageBackground />

      {/* An eCRF extraction is minutes of work with a real, reportable shape —
          chunks within passes — so the bar is a real bar. The resume loop runs
          in THIS tab, which is why the "keep this tab open" rule is on: closing
          it stops the loop mid-way and leaves the eCRF partial (recoverable
          from Resume, but only after somebody notices). */}
      <StepProgressOverlay
        open={busy || !!stalledError}
        title="Reading the eCRF"
        detail={
          progress
            ? `Pass ${progress.pass} · ${progress.chunksComplete}/${progress.chunkCount} chunks`
            : "Uploading…"
        }
        value={
          progress?.chunkCount
            ? progress.chunksComplete / progress.chunkCount
            : null
        }
        typical="usually a few minutes"
        keepTabOpen
        error={stalledError}
        retryLabel="Resume"
        onRetry={() => stalledId && extract(stalledId)}
        onDismiss={() => setStalledError("")}
      />
      <div className="max-w-6xl mx-auto flex flex-col gap-4">
        {/* Header */}
        <div className={`${card} flex items-center justify-between px-4 py-3`}>
          <div className="flex items-center gap-2 min-w-0">
            {/* Back sits top-left on every SourceX page. */}
            <button
              onClick={() => navigate(-1)}
              className="shrink-0 inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-white/[0.04] border border-white/10 hover:bg-white/[0.08] text-xs text-slate-300 hover:text-white transition"
            >
              <ArrowLeft className="w-3.5 h-3.5" /> Back
            </button>
            <FileText className="w-5 h-5 text-purple-300 shrink-0" />
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-[0.22em] text-purple-300/80 font-semibold leading-none">
                Step 2 · Blank eCRF
              </div>
              <h2 className="text-base font-semibold text-white truncate">
                {studyId}
              </h2>
            </div>
          </div>
          {PROTOCOL_COMPARE_ENABLED && (
            <AiHelperNotice compact className="w-full sm:w-auto">The eCRF's forms and questions are read by AI.</AiHelperNotice>
          )}
          {/* Only once something has been uploaded. On an empty study the one
              big button in the middle of the page IS the upload control, and a
              second one up here would split the only decision on screen in
              two. */}
          {!loading && list.length > 0 && (
            <UploadButton
              busy={busy}
              onFile={handleUpload}
              className={`shrink-0 inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-medium text-white transition ${
                busy
                  ? "bg-white/10"
                  : "bg-white/[0.06] border border-white/12 hover:bg-white/[0.12]"
              }`}
            >
              {busy && <Loader2 className="w-4 h-4 animate-spin" />}
              {busy ? "Working…" : "Upload another"}
            </UploadButton>
          )}
        </div>

        {ECRF_BATCH_ENABLED && (
          <label className="flex items-start gap-2 text-xs text-slate-400 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={queueBatch}
              onChange={(ev) => setQueueBatch(ev.target.checked)}
              disabled={busy}
              className="mt-0.5 accent-blue-400"
            />
            <span>
              <span className="text-slate-300">Queue this read instead of waiting</span> — about
              half the cost, results usually within a few hours. You can close the page; it
              collects itself next time you open it.
            </span>
          </label>
        )}

        {error && (
          <div className="rounded-xl border border-rose-400/30 bg-rose-500/[0.08] px-3 py-2 text-sm text-rose-200 flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {/* Loading / empty state. The empty state IS the page: one thing to do,
            in the middle of the screen, no icon and no list of nothing under
            it. */}
        {loading && (
          <div className="flex items-center justify-center gap-2 py-24 text-sm text-slate-400">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        )}

        {!loading && list.length === 0 && (
          <div className="flex flex-col items-center justify-center text-center py-20">
            {/* Step 1 first. These steps are ordered for a reason — the mapping
                joins the protocol's schedule to this eCRF's forms, so an eCRF
                uploaded on its own has nothing to be mapped against. Rather
                than let somebody spend a long extraction on a study that cannot
                use it, the button is dead until step 1 has been done, and says
                which step is missing instead of just being grey. */}
            {step1Done ? (
              <UploadButton
                busy={busy}
                onFile={handleUpload}
                // The same green as step 1's hero. These are the same act on two
                // pages — "put the file in" — and giving them different colours
                // made step 2 look like a different kind of thing.
                className={`inline-flex items-center justify-center px-12 py-7 rounded-3xl text-lg font-bold text-white transition ${
                  busy
                    ? "bg-white/10"
                    : "bg-gradient-to-r from-emerald-500 to-teal-500 ring-2 ring-emerald-300/50 shadow-[0_18px_56px_-12px_rgba(16,185,129,0.8)] hover:shadow-[0_22px_68px_-10px_rgba(16,185,129,0.95)] hover:brightness-110"
                }`}
              >
                {busy && <Loader2 className="w-5 h-5 animate-spin mr-2" />}
                {busy ? "Working…" : "Upload Blank eCRF (PDF)"}
              </UploadButton>
            ) : (
              <>
                <button
                  disabled
                  title={
                    protocolAwaitingApproval
                      ? "Approve the protocol's schedule of assessments first — step 1"
                      : "Upload the protocol first — step 1"
                  }
                  className="inline-flex items-center justify-center px-12 py-7 rounded-3xl text-lg font-bold bg-white/[0.06] border border-white/10 text-slate-500 cursor-not-allowed"
                >
                  Upload Blank eCRF (PDF)
                </button>
                <p className="mt-4 text-sm text-amber-200/90">
                  {protocolAwaitingApproval
                    ? "The protocol is uploaded but its schedule of assessments has not been approved yet. Everything built from here inherits that reading, so approve it first."
                    : "Upload the protocol first."}
                </p>
                <button
                  onClick={() => navigate(`/sourcex/protocols/${studyId}`)}
                  className="mt-3 inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-500 text-sm font-semibold text-white hover:brightness-110 transition"
                >
                  {protocolAwaitingApproval
                    ? "Go to Step 1 · Review & Approve"
                    : "Go to Step 1 · Upload Protocol"}{" "}
                  <ChevronRight className="w-4 h-4" />
                </button>
              </>
            )}
            <p className="mt-4 text-xs text-slate-500">
              Read once into forms, fields, option lists and branching logic.
            </p>
          </div>
        )}

        {/* Pass/chunk progress used to be printed here, and again on a card
            below, and again inside the "uploaded" modal. All three only ever
            rendered while `busy` — which is exactly when StepProgressOverlay
            covers the page — so none of them could be seen. The overlay shows
            the same counter, plus a bar driven by the same fraction. */}

        {/* eCRF list */}
        {list.length > 0 && (
          <div className={`${card} p-4`}>
            <h3 className="font-semibold text-white text-sm mb-3">
              Uploaded eCRFs
            </h3>
            <div className="space-y-1">
              {list.map((e) => (
                <div
                  key={e.id}
                  className={`flex flex-wrap items-center gap-2 rounded-lg px-3 py-2 text-sm border transition ${
                    selectedId === e.id
                      ? "bg-purple-500/15 border-purple-300/30"
                      : "border-transparent hover:bg-white/[0.05]"
                  }`}
                >
                  <button
                    onClick={() => setSelectedId(e.id)}
                    className="flex-1 text-left min-w-0"
                  >
                    <span className="block truncate text-slate-200">
                      {e.fileName || e.id}
                    </span>
                    {/* When, and by whom. Both were already written on every
                        upload (createdBy / uploadedAt); nothing showed them.
                        Same line, same wording as step 1's protocol list. */}
                    <UploadedLine item={e} />
                  </button>
                  {/* A pill only while something is still happening, or has
                      gone wrong. A finished extraction says nothing — see
                      SETTLED_STATUSES. */}
                  {!SETTLED_STATUSES.has(e.status) && <StatusPill status={e.status} />}
                  {e.pageCount ? (
                    <span className="text-[11px] text-slate-500">
                      {e.pageCount}p
                    </span>
                  ) : null}
                  {/* What was actually uploaded. The one thing a coordinator
                      might genuinely want from a finished row — the PDF itself
                      — in place of a status telling them to do a job that does
                      not exist here. `noopener` because this opens a Storage
                      URL in a tab of its own. */}
                  {e.downloadUrl && (
                    <a
                      href={e.downloadUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      title="Open the uploaded PDF in a new tab"
                      className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-white/[0.04] border border-white/12 hover:bg-white/[0.1] text-xs text-slate-300 hover:text-white transition"
                    >
                      <Eye className="w-3 h-3" /> View
                    </a>
                  )}
                  {(e.status === ECRF_STATUS.FAILED || e.status === "partial") && (
                    <button
                      onClick={() => extract(e.id)}
                      disabled={busy}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-white/[0.06] border border-white/10 hover:bg-white/[0.12] text-xs disabled:opacity-40 transition"
                    >
                      <RefreshCw className="w-3 h-3" />
                      {e.status === "partial" ? "Resume" : "Retry"}
                    </button>
                  )}
                  {/* A queued read collects itself when the page loads; this is
                      for somebody who is already looking at it and does not want
                      to reload. */}
                  {e.status === ECRF_STATUS.BATCH_PENDING && (
                    <button
                      onClick={() => checkBatch(e.id)}
                      disabled={busy || collecting}
                      title="Check whether the queued read has finished"
                      className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-white/[0.06] border border-white/10 hover:bg-white/[0.12] text-xs disabled:opacity-40 transition"
                    >
                      {collecting ? (
                        <Loader2 className="w-3 h-3 animate-spin" />
                      ) : (
                        <RefreshCw className="w-3 h-3" />
                      )}
                      Check now
                    </button>
                  )}
                </div>
              ))}
            </div>
            {selected?.errorMessage && (
              <p className="mt-2 text-xs text-rose-300">{selected.errorMessage}</p>
            )}
          </div>
        )}

        {/* What the extraction produced, as two small buttons rather than the
            rest of the page.

            The counts, the forms, the fields, the confidence scores and the
            warnings are all real and all worth keeping — but they are notes for
            whoever tunes the pipeline, not a coordinator's job, and none of
            them blocks step 3. Spread down the page they read as "something is
            wrong here"; behind two buttons they are there when wanted and out
            of the way of the only thing that matters, which is the green bar at
            the foot of this page. */}
        {stats && (
          <div className={`${card} px-4 py-3 flex flex-wrap items-center gap-2`}>
            <button
              onClick={() => setFormsOpen(true)}
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-white/12 bg-white/[0.05] text-[11px] text-slate-300 hover:bg-white/[0.12] hover:text-white transition"
            >
              <FileText className="w-3.5 h-3.5" />
              Show extracted forms ({stats.forms})
            </button>
            {warnings.length > 0 && (
              <button
                onClick={() => setWarningsOpen(true)}
                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-amber-400/25 bg-amber-400/[0.08] text-[11px] text-amber-200/90 hover:bg-amber-400/20 hover:text-white transition"
              >
                <AlertTriangle className="w-3.5 h-3.5" />
                Show warnings ({warnings.length})
              </button>
            )}
          </div>
        )}

        {extraction &&
          (extraction.forms || []).length === 0 &&
          selected?.status === ECRF_STATUS.NEEDS_REVIEW && (
            <div className={`${card} p-4 text-sm text-slate-400`}>
              Extraction finished but found no forms. The PDF may be scanned
              without a text layer, or may not be a blank eCRF.
            </div>
          )}

        {/* The way out, pinned under the page once there is anything here. The
            landing modal says the same thing, but it is dismissable and this is
            not — somebody who closed it and scrolled still has one click to
            wherever they meant to be. */}
        {!loading && (list.length > 0 || hasAssembled) && (
          <motion.button
            whileHover={hasAssembled ? { y: -2 } : undefined}
            whileTap={hasAssembled ? { scale: 0.99 } : undefined}
            onClick={() => navigate(nextStep.to)}
            className={`w-full flex items-center gap-3 px-5 rounded-2xl transition text-left ${
              hasAssembled
                ? "py-5 bg-gradient-to-r from-emerald-500 to-teal-500 ring-2 ring-emerald-300/50 shadow-[0_14px_44px_-10px_rgba(16,185,129,0.75)] hover:shadow-[0_18px_56px_-8px_rgba(16,185,129,0.95)] hover:brightness-110"
                : "py-4 border border-emerald-400/30 bg-emerald-400/[0.08] hover:bg-emerald-400/[0.15] hover:border-emerald-300/50"
            }`}
          >
            <span className="min-w-0 flex-1">
              <span
                className={`block font-semibold ${
                  hasAssembled ? "text-lg font-bold text-white" : "text-sm text-emerald-100"
                }`}
              >
                {nextStep.label}
              </span>
              <span
                className={`block text-[11px] mt-0.5 ${
                  hasAssembled ? "text-emerald-50/90" : "text-emerald-100/70"
                }`}
              >
                {nextStep.hint}
              </span>
            </span>
            <ChevronRight
              className={`shrink-0 ${
                hasAssembled ? "w-6 h-6 text-white" : "w-5 h-5 text-emerald-200"
              }`}
            />
          </motion.button>
        )}
      </div>

      <AnimatePresence>
        {/* Landed on step 2 with step 2 already done. One line, and it leads
            with the step they actually want; re-uploading stays available, just
            not as the loud option. */}
        {landingOpen && (
          <Modal
            key="landing"
            onClose={() => setLandingOpen(false)}
            title={
              hasAssembled
                ? "Source documents are already built."
                : "An eCRF is already uploaded."
            }
            footer={
              <>
                <UploadButton
                  busy={busy}
                  onFile={handleUpload}
                  className={`${quietBtn} ${busy ? "opacity-60" : ""}`}
                >
                  Re-upload
                </UploadButton>
                <button
                  onClick={() => {
                    setLandingOpen(false);
                    navigate(nextStep.to);
                  }}
                  className={primaryBtn}
                >
                  {nextStep.label} <ChevronRight className="w-4 h-4" />
                </button>
              </>
            }
          />
        )}

        {/* Just uploaded, and the extraction has FINISHED. While it is running
            the overlay owns the screen, so this only ever renders once `busy`
            has cleared — which is why it no longer carries a "reading…" state
            of its own. All that is left to say is where to go next. */}
        {uploaded && !busy && (
          <Modal
            key="uploaded"
            onClose={() => setJustUploadedId(null)}
            title={
              uploaded.status === ECRF_STATUS.FAILED
                ? "Extraction failed."
                : "eCRF read."
            }
            footer={
              <>
                <button onClick={() => setJustUploadedId(null)} className={quietBtn}>
                  Stay here
                </button>
                {uploaded.status === ECRF_STATUS.FAILED ? (
                  <button onClick={() => extract(uploaded.id)} className={primaryBtn}>
                    <RefreshCw className="w-4 h-4" /> Retry
                  </button>
                ) : (
                  <button
                    onClick={() => {
                      setJustUploadedId(null);
                      navigate(nextStep.to);
                    }}
                    className={primaryBtn}
                  >
                    {nextStep.label} <ChevronRight className="w-4 h-4" />
                  </button>
                )}
              </>
            }
          >
            {uploaded.status === ECRF_STATUS.FAILED ? (
              <p className="text-rose-300/90">
                {uploaded.errorMessage || "The eCRF could not be read."}
              </p>
            ) : (
              <p>The forms and fields are below if you want to check them.</p>
            )}
          </Modal>
        )}

        {/* What the extraction actually read, on demand: the counts first, then
            every form, expandable down to its fields. Same content as the old
            page-length section, in a place that does not stand between the
            coordinator and the next step. */}
        {formsOpen && stats && (
          <Modal
            key="forms"
            wide
            onClose={() => setFormsOpen(false)}
            title="Extracted forms"
            footer={
              <button onClick={() => setFormsOpen(false)} className={quietBtn}>
                Close
              </button>
            }
          >
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <Stat label="Forms" value={stats.forms} />
              <Stat label="Fields" value={stats.fields} />
              <Stat
                label="Low confidence"
                value={stats.lowConf}
                tone={stats.lowConf > 0 ? "warn" : "ok"}
              />
              <Stat
                label="Dropped branches"
                value={stats.unresolved}
                tone={stats.unresolved > 0 ? "warn" : "ok"}
              />
            </div>

            {(extraction?.forms || []).length > 0 ? (
              <div className="mt-4 space-y-2">
                {extraction.forms.map((form) => {
                  const open = !!openForms[form.form_id];
                  return (
                    <div
                      key={form.form_id}
                      className="rounded-xl border border-white/10 overflow-hidden"
                    >
                      <button
                        onClick={() =>
                          setOpenForms((st) => ({
                            ...st,
                            [form.form_id]: !st[form.form_id],
                          }))
                        }
                        className="w-full flex items-center gap-2 px-3 py-2 bg-white/[0.03] hover:bg-white/[0.06] transition text-left"
                      >
                        {open ? (
                          <ChevronDown className="w-4 h-4 text-slate-400 shrink-0" />
                        ) : (
                          <ChevronRight className="w-4 h-4 text-slate-400 shrink-0" />
                        )}
                        <span className="font-medium text-slate-100 truncate">
                          {form.name}
                        </span>
                        <span className="text-[11px] text-slate-500 shrink-0">
                          {(form.fields || []).length} fields
                        </span>
                        {form.extract_method !== "text" && (
                          <span className="inline-flex items-center gap-1 text-[10px] text-blue-200 bg-blue-400/10 border border-blue-400/25 rounded px-1.5 py-0.5 shrink-0">
                            <Eye className="w-3 h-3" /> {form.extract_method}
                          </span>
                        )}
                        {form.confidence < LOW_CONFIDENCE_THRESHOLD && (
                          <span className="text-[10px] text-amber-200 bg-amber-400/10 border border-amber-400/25 rounded px-1.5 py-0.5 shrink-0">
                            {Math.round(form.confidence * 100)}%
                          </span>
                        )}
                        <span className="ml-auto text-[11px] text-slate-600 shrink-0">
                          p.{(form.source_pages || []).join(", ")}
                        </span>
                      </button>

                      {open && (
                        <div className="divide-y divide-white/5">
                          {(form.fields || []).map((f) => (
                            <FieldRow key={f.field_id} field={f} form={form} />
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="mt-4 text-sm text-slate-400">
                No forms were found in this eCRF.
              </p>
            )}

            {/* What the run cost. Diagnostics for whoever tunes the pipeline —
                it describes the extraction that produced the forms above, so it
                belongs with them rather than on the page. */}
            {stats.usage && (
              <div className="mt-4 pt-3 border-t border-white/10 text-xs text-slate-400">
                <span className="font-medium text-slate-300">This run:</span>{" "}
                {stats.usage.totalCalls} model calls ·{" "}
                {stats.usage.totalInputTokens.toLocaleString()} in /{" "}
                {stats.usage.totalOutputTokens.toLocaleString()} out · approx{" "}
                {money(stats.usage.estimatedCostUsd)}
                {stats.usage.costComplete ? "" : "+"}
                {Object.keys(stats.usage.byStage || {}).length > 0 && (
                  <span className="ml-2 text-slate-500">
                    (
                    {Object.entries(stats.usage.byStage)
                      .map(([k, v]) => `${k.replace("ecrf_", "")}: ${v.calls}`)
                      .join(", ")}
                    )
                  </span>
                )}
                <div className="mt-1 text-slate-600">
                  Cost is an estimate for comparing stages — reconcile against
                  the provider invoice before treating it as billing.
                </div>
              </div>
            )}
          </Modal>
        )}

        {/* Every complaint the extraction has, on demand. None of it blocks
            step 3 and none of it is a coordinator's job — see the `warnings`
            memo. */}
        {warningsOpen && (
          <Modal
            key="warnings"
            wide
            onClose={() => setWarningsOpen(false)}
            title={`${warnings.length} warning${warnings.length === 1 ? "" : "s"}`}
            footer={
              <button onClick={() => setWarningsOpen(false)} className={quietBtn}>
                Close
              </button>
            }
          >
            <p className="mb-3 text-xs text-slate-500">
              Notes from the extraction. None of them stops you continuing.
            </p>
            <ul className="space-y-2">
              {warnings.map((w, i) => (
                // Warnings carry no id and are display-only.
                // eslint-disable-next-line react/no-array-index-key
                <li
                  key={i}
                  className={`rounded-xl border px-3 py-2 text-xs ${
                    w.tone === "bad"
                      ? "border-rose-400/30 bg-rose-500/[0.08] text-rose-100/90"
                      : "border-amber-400/25 bg-amber-400/[0.07] text-amber-100/85"
                  }`}
                >
                  <span
                    className={`mr-1.5 font-semibold ${
                      w.tone === "bad" ? "text-rose-300/80" : "text-amber-300/80"
                    }`}
                  >
                    [{w.type}]
                  </span>
                  {w.message}
                </li>
              ))}
            </ul>
          </Modal>
        )}
      </AnimatePresence>
    </div>
  );
}

const Stat = ({ label, value, tone }) => (
  <div className="rounded-xl border border-white/10 bg-white/[0.02] px-3 py-2">
    <div className="text-[10px] uppercase tracking-wider text-slate-500">
      {label}
    </div>
    <div
      className={`text-lg font-semibold ${
        tone === "warn"
          ? "text-amber-300"
          : tone === "ok"
          ? "text-emerald-300"
          : "text-white"
      }`}
    >
      {value}
    </div>
  </div>
);

const FieldRow = ({ field, form }) => {
  const controller =
    field.visible_when &&
    (form.fields || []).find((x) => x.field_id === field.visible_when.field_id);

  return (
    <div className="px-3 py-2">
      <div className="flex items-start gap-2">
        <span className="flex-1 text-sm text-slate-200">
          {field.label}
          {field.required && <span className="ml-1 text-rose-300">*</span>}
        </span>
        <span className="text-[10px] uppercase tracking-wide text-slate-500 shrink-0">
          {field.type}
        </span>
        {field.confidence < LOW_CONFIDENCE_THRESHOLD && (
          <span className="text-[10px] text-amber-200 bg-amber-400/10 border border-amber-400/25 rounded px-1.5 py-0.5 shrink-0">
            {Math.round(field.confidence * 100)}%
          </span>
        )}
      </div>

      {field.options?.length > 0 && (
        <div className="mt-0.5 text-xs text-slate-500 truncate">
          {field.options.join(" · ")}
        </div>
      )}
      {field.unit && (
        <div className="mt-0.5 text-xs text-slate-500">unit: {field.unit}</div>
      )}
      {field.group_name && (
        <div className="mt-0.5 text-xs text-slate-500">
          repeating group: {field.group_name}
        </div>
      )}

      {/* Branching is the highest-value thing on the page, so it is shown
          explicitly rather than buried — including when it was DROPPED, which
          silently changes what a coordinator will see. */}
      {field.visible_when && (
        <div className="mt-1 inline-flex items-start gap-1.5 text-xs text-purple-200/90">
          <GitBranch className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <span>
            shown when{" "}
            <span className="text-purple-100">
              “{controller ? controller.label : field.visible_when.field_id}”
            </span>{" "}
            {field.visible_when.op}
            {field.visible_when.value !== undefined && (
              <>
                {" "}
                <span className="text-purple-100">
                  {Array.isArray(field.visible_when.value)
                    ? field.visible_when.value.join(" / ")
                    : String(field.visible_when.value)}
                </span>
              </>
            )}
          </span>
        </div>
      )}
      {field.branch_dropped && (
        <div className="mt-1 inline-flex items-center gap-1.5 text-xs text-amber-200/90">
          <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
          conditional in the eCRF, but the controlling question could not be
          matched — always shown
        </div>
      )}
    </div>
  );
};
